/**
 * Route registry — one entry per client-area screen, with the i18n key of its
 * visible `<h1>`. Drives the browser-tab title (`DocumentTitle`), the route-change
 * announcement and focus move (`RouteAnnouncer`) and the guard that stops a new
 * `<Route path>` from shipping without a title (#3621).
 *
 * `titleKey` is `namespace:key` and MUST be the key the page itself renders in its
 * `<h1>`, so the tab, the announcement and the heading can never disagree
 * (a rename of the screen follows through the shared key).
 *
 * Routes that are not here yet live in `PENDING_ROUTES`. That list only goes DOWN:
 * the guard `every-route-is-registered.test.ts` fails when a route is in neither
 * list, and when a route is in both.
 */

import { matchPath } from 'react-router-dom'

export interface RouteMeta {
  id: string
  pattern: string
  titleKey: string
  section: 'public' | 'customer' | 'org-admin' | 'super-admin'
}

export const ROUTE_REGISTRY: readonly RouteMeta[] = [
  {
    id: 'transactions',
    pattern: '/transactions',
    titleKey: 'transactions:accessibility.pageHeading',
    section: 'customer',
  },
  { id: 'balances', pattern: '/balances', titleKey: 'balance:page.title', section: 'customer' },
  { id: 'import', pattern: '/import', titleKey: 'imports:page.title', section: 'customer' },
  {
    id: 'import-custom-create',
    pattern: '/import/custom',
    titleKey: 'imports:customImporter.userPage.title',
    section: 'customer',
  },
  {
    id: 'import-custom-edit',
    pattern: '/import/custom/:templateId',
    titleKey: 'imports:customImporter.userPage.title',
    section: 'customer',
  },
  { id: 'matching', pattern: '/matching', titleKey: 'matching:page.title', section: 'customer' },
  { id: 'prices', pattern: '/prices', titleKey: 'portfolio:prices.page.title', section: 'customer' },
  {
    id: 'tax-report',
    pattern: '/tax-report',
    titleKey: 'tax-reports:page.title',
    section: 'customer',
  },
  { id: 'support', pattern: '/support', titleKey: 'support:myTickets.title', section: 'customer' },
  {
    id: 'support-ticket',
    pattern: '/support/:ticketId',
    titleKey: 'support:detail.pageTitle',
    section: 'customer',
  },
  { id: 'profile', pattern: '/profile', titleKey: 'profile:page.title', section: 'customer' },
  {
    id: 'settings-security',
    pattern: '/settings/security',
    titleKey: 'profile:page.title',
    section: 'customer',
  },
  {
    id: 'settings-email',
    pattern: '/settings/email',
    titleKey: 'profile:page.title',
    section: 'customer',
  },
]

export interface PendingRoute {
  /** The `<Route path>` literal, or a prefix ending in `/*` for a whole area. */
  pattern: string
  /** Which later piece of #3621 registers it. */
  piece: 'b' | 'c'
}

export const PENDING_ROUTES: readonly PendingRoute[] = [
  // piece b — public access routes (their titles need new keys in the 10 languages)
  { pattern: '/', piece: 'b' },
  { pattern: '/login', piece: 'b' },
  { pattern: '/register', piece: 'b' },
  { pattern: '/waitlist/confirm', piece: 'b' },
  { pattern: '/waitlist/unsubscribe', piece: 'b' },
  { pattern: '/forgot-password', piece: 'b' },
  { pattern: '/reset-password', piece: 'b' },
  { pattern: '/verify-email', piece: 'b' },
  { pattern: '/magic-link', piece: 'b' },
  { pattern: '/magic-login/:token', piece: 'b' },
  { pattern: '/unauthorized', piece: 'b' },
  { pattern: '*', piece: 'b' },
  // piece c — organisation and super-admin panels (190 screens)
  { pattern: '/admin/*', piece: 'c' },
]

const segmentsOf = (pattern: string): string[] => pattern.split('/').filter(Boolean)

/** Static segments weigh more than parameters: `/import/custom` beats `/import/:x`. */
const specificity = (pattern: string): number =>
  segmentsOf(pattern).reduce((sum, segment) => sum + (segment.startsWith(':') ? 1 : 100), 0)

/**
 * The most specific registered route for `pathname`, or `null`. Specificity, not
 * declaration order, decides: `/support/42` is the ticket, not `/support`.
 */
export function getRouteMeta(pathname: string): RouteMeta | null {
  let best: RouteMeta | null = null
  for (const meta of ROUTE_REGISTRY) {
    if (!matchPath({ path: meta.pattern, end: true }, pathname)) continue
    if (best === null || specificity(meta.pattern) > specificity(best.pattern)) best = meta
  }
  return best
}

/** Is `pathname` a route that exists but is not registered yet? `*` never matches here. */
export function isPendingRoute(pathname: string): boolean {
  return PENDING_ROUTES.some(
    (route) => route.pattern !== '*' && matchPath({ path: route.pattern, end: true }, pathname),
  )
}
